import { importImages } from 'theme/utils';
import type { Theme } from './contracts';

const images = importImages(
  require.context('./images', false, /\.(png|jpe?g|svg)$/),
  require.context('!@svgr/webpack!./images', true, /\.svg$/)
);

const menu = importImages(
  require.context('./images/menu', false, /\.(png|jpe?g|svg)$/),
  require.context('!@svgr/webpack!./images/menu', true, /\.svg$/)
);

const numbers = importImages(
  require.context('./images/numbers', false, /\.(svg)$/),
  require.context('!@svgr/webpack!./images/numbers', true, /\.svg$/)
);

const theme: Theme = {
  mainFontFamily: 'Open Sans',
  mainFontFamilyUrl:
    'https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;700;800&display=swap',

  //main
  greenColor: '#00B83E',
  darkGreenColor: '#00B83E66',
  marineColor: '#00B67D',
  lavenderColor: '#514DDB',
  blueColor: '#02A7DB',

  //additional
  appleColor: '#85C300',
  pinkColor: '#F267BA',
  neonPink: '#F04DAF',
  pinkLightColor: '#CF098C',
  rougeColor: '#D6385E',
  redColor: '#E73700',
  orangeColor: '#F05803',
  flameOrange: '#EA5B0C',
  carrotColor: '#E76F00',
  yellowColor: '#FFCB00',
  yellowDarkColor: '#F0C018',
  violetColor: '#A157FF',
  goldenSun: '#FFDD64',
  darkPink: '#B81D7A',

  // grayscale
  whiteColor: '#FFFFFF',
  darkWhiteColor: '#FFFFFF99',
  grayColor1: '#9A9CAC',
  grayColor2: '#2E303C',
  grayColor3: '#22232C',
  grayColor4: '#0E111A',
  grayColor5: '#1F202C',
  grayColor6: '#323444',
  grayColor7: '#ffffff66',
  grayColor8: '#242632',
  grayColor9: '#3E4148',
  grayColor10: '#6E7076',
  grayColor11: '#282939',
  grayColor12: '#272836',
  blackColor: '#000000',

  //gradients
  greenMarineGradient: 'linear-gradient(90deg, #00B83E 0%, #00B67D 100%)',
  goldenOrangeGradient: 'linear-gradient(90deg, #FF7B00 0%, #FFCB00 100%)',
  smokyBlueGradient: 'linear-gradient(90deg, #9A9CAC 0%, #CED0DA 100%)',
  electricVioletGradient: 'linear-gradient(90deg, #514DDB 0%, #A157FF 100%)',
  springWaveGradient: 'linear-gradient(90deg, #00B83E 0%, #85C300 100%)',
  oceanBreezeGradient: 'linear-gradient(90deg, #065DDF 0%, #02A7DB 100%)',
  blueViolet:
    'linear-gradient(90deg, rgba(81, 77, 219, 0.15) 0%, rgba(2, 167, 219, 0.15) 100%)',
  lavenderBlueGradient: 'linear-gradient(90deg, #514DDB 0%, #02A7DB 100%)',
  greenAppleGradient: 'linear-gradient(90deg, #00B83E 0%, #85C400 100%)',
  pinkRougeGradient: 'linear-gradient(90deg, #B81D7A 0%, #D6385E 100%)',
  redOrangeGradient: 'linear-gradient(90deg, #E72A00 0%, #F05803 100%)',
  neonPinkGradient: 'linear-gradient(90deg, #B81D7A 1.28%, #D6385E 100%)',
  darkGradient:
    'linear-gradient(179deg, rgba(31, 32, 44, 0.00) 1.03%, #1F202C 54.74%)',
  ambientDark: 'radial-gradient(50% 50% at 50% 50%, #15161E 0%, #191A24 100%)',
  carrotYellowGradient: 'linear-gradient(90deg, #E76F00 0%, #FFCB00 100%)',
  whiteGradient30_0:
    'linear-gradient(90deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0) 100%);',
  whiteGradient10_0:
    'linear-gradient(90deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)',
  cloudGradient:
    'linear-gradient(90deg,rgba(6, 93, 223, 1) 0%,rgba(2, 167, 219, 1) 100%)',
  greenGradient:
    'linear-gradient(90deg,rgba(0, 184, 62, 1) 0%,rgba(133, 195, 0, 1) 100%)',
  darkGreenGradient: 'linear-gradient(180deg, #1F202C 0%, #25D366 100%)',
  purpleGradient:
    'linear-gradient(90deg,rgba(81, 77, 219, 1) 0%,rgba(161, 87, 255, 1) 100%)',
  pinkGradient:
    'linear-gradient(90deg,rgba(184, 29, 122, 1) 0%,rgba(214, 56, 94, 1) 100%)',
  orangeGradient:
    'linear-gradient(90deg,rgba(231, 42, 0, 1) 0%,rgba(240, 88, 3, 1) 100%)',
  yellowGradient:
    'linear-gradient(90deg,rgba(255, 123, 0, 1) 0%,rgba(255, 203, 0, 1) 100%)',
  platinumGradient:
    'linear-gradient(90deg,rgba(154, 156, 172, 1) 0%,rgba(206, 208, 218, 1) 100%)',
  grayGradient: 'linear-gradient(90deg, #75787d, rgba(255, 255, 255, 0) 100%)',
  emeraldGradient: 'linear-gradient(90deg, #00b83e 0%, #00b67d 100%)',
  mintGradient: 'linear-gradient(90deg, #48cc78, rgba(255, 255, 255, 0) 100%)',

  //old
  mainColor: '#88B009',
  greenLightColor: '#99c50e',

  secondaryColor: '#9A9CAC',
  secondaryColor1: '#6B6D7A',
  secondaryColor2: '#2E303C',
  secondaryColor3: '#22232C',
  secondaryColor4: '#191B1F',
  secondaryColor5: '#111318',
  secondaryColor6: '#6b6d7a',

  redColorOld: '#F25523',
  blueColorOld: '#1ABBDE',
  gradientColor: '#983F03',

  mainBackdropFilter: 'blur(24px)',
  secondaryBackdropFilter: 'blur(30px)',
  secondaryBackdropFilter2: 'blur(20px)',
  secondaryBackdropFilter3: 'blur(15px)',

  // image collections
  images,
  menu,
  numbers,

  preloaderSpinnerAnimation: images.preloaderSpinnerAnimation.component,
  preloaderDotsAnimation: images.preloaderDotsAnimation.component,

  arrowLeftIcon: images.arrowLeftIcon.component,
  arrowRightSmallIcon: images.arrowRightSmallIcon.component,
  arrowRightIcon: images.arrowRightIcon.component,
  searchIcon: images.searchIcon.component,
  mobileAppIcon: images.mobileAppIcon.component,
  arrowSelectIcon: images.arrowSelectIcon.component,
  smoothArrowIcon: images.smoothArrowIcon.component,
  arrowScalableIcon: images.arrowScalableIcon.component,

  supportIcon: images.supportIcon.component,
  hamburgerIcon: images.hamburgerIcon.component,
  promoIcon: images.promoIcon.component,
  search2Icon: images.search2Icon.component,
  search3Icon: images.search3Icon.component,
  filterIcon: images.filterIcon.component,

  arrowRightIconSharp: images.arrowRightIconSharp.path,
  arrowBottomIconSharpSmall: images.arrowBottomIconSharpSmall.component,

  eyeClosedIcon: images.eyeClosedIcon.path,
  eyeOpenIcon: images.eyeOpenIcon.path,

  copyIcon: images.copyIcon.component,

  polygon: images.polygon.path,
  levelBackground: images.levelBackground.path,
  levelBackgroundNew: images.levelBackgroundNew.path,

  additionalInfoIcon: images.additionalInfoIcon.component,
  depNumberIcon: images.depNumberIcon.component,
  frequencyIcon: images.frequencyIcon.component,
  tickIcon: images.tickIcon.component,
  sharpTickIcon: images.sharpTickIcon.component,
  lockSharpIcon: images.lockSharpIcon.component,
  gemIcon: images.gemIcon.component,
  fsAmountIcon: images.fsAmountIcon.component,
  gameIcon: images.gameIcon.component,
  maxBonusAmountIcon: images.maxBonusAmountIcon.component,
  maxWinFsIcon: images.maxWinFsIcon.component,
  minDepIcon: images.minDepIcon.component,
  wagerIcon: images.wagerIcon.component,
  wagerTimeIcon: images.wagerTimeIcon.component,
  presentIcon: images.presentIcon.component,
  present2Icon: images.present2Icon.component,
  goldGiftIcon: images.goldGiftIcon.component,

  linkIcon: images.linkIcon.path,
  lockIcon: images.lockIcon.path,

  crossIcon: images.crossIcon.component,
  crossIcon2: images.crossIcon2.component,
  crossIconSmall: images.crossIconSmall.component,
  crossIcon3: images.crossIcon3.component,
  crossSmallIcon: images.crossSmallIcon.component,
  favoritesIcon: images.favoritesIcon.component,
  favoritesLabelIcon: images.favoritesLabelIcon.component,
  recentIcon: images.recentIcon.component,
  fullScreenIcon: images.fullScreenIcon.component,

  successIcon: images.successIcon.component,
  infoIcon: images.infoIcon.component,
  infoIconSmall: images.infoIconSmall.component,
  infoSymbol: images.infoSymbol.component,
  warningIcon: images.warningIcon.component,
  errorIcon: images.errorIcon.component,

  benefit1: images.benefit1.component,
  benefit2: images.benefit2.component,
  benefit3: images.benefit3.component,

  noTournamentIcon: images.noTournamentIcon.component,

  circleInfoIcon: images.circleInfoIcon.component,

  zoomIcon: images.zoomIcon.component,
  plusIcon: images.plusIcon.component,
  minusIcon: images.minusIcon.component,
};

export default theme;
